// src/components/resources/tools/generators/Notification/NotificationGenerator.tsx

/**
 * @fileoverview Notification / Lock-Screen Generator
 * @description Renders pixel-accurate push-notification mockups across the three
 * styles marketers and designers actually use in ad creative and product shots:
 *   - iOS Lock Screen  (wallpaper + big clock/date + a frosted notification card)
 *   - iOS Banner       (the top-of-screen pill that slides in over any screen)
 *   - Android (Material) (status bar + a Material notification card)
 *
 * The "📱 you just got a notification" hook is one of the most-used ad creative
 * formats, and OS notification chrome is exactly the version-specific detail
 * generic tools get wrong (wrong status-bar glyphs, battery percentage drawn
 * beside the battery instead of inside it, mismatched corner radii). This tool
 * reuses the real {@link IosStatusBar} primitive shared with the chat previews
 * so the status row is correct, and exports a clean PNG via the shared
 * captureAndDownload helper.
 *
 * Demo-trust: defaults are neutral placeholders (generic app name, template
 * copy) so an unedited export can't be mistaken for a real alert from a real
 * brand.
 *
 * @module Notification
 */

'use client'

import { useCallback, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { toast } from 'sonner'
import { PhotoIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import { createEngagementTimers, sendResourceEvent } from '~/lib/gtag'
import { captureAndDownload } from '../shared/exportImage'
import { ExportButton } from '../shared/ExportButton'
import { useExport } from '../shared/useExport'
import { waitForStable } from '../shared/waitForStable'
import { usePreviewDeferral, useStaleRef } from '../shared/usePreviewDeferral'
import { IosStatusBar } from '../FakeChat/IosStatusBar'

/**
 * Self-hosted Google Sans (Pixel's system font) for the Android frame. Declared
 * as a route-scoped `<style>` rather than a global CSS import: a global font
 * import leaks the stylesheet chunk into every page's <head>, which churns the
 * whole sitemap's `lastmod`. Inline @font-face stays on this route only (and the
 * sitemap content-fingerprint normalizer strips `<style>`, so it adds zero
 * fingerprint noise). Files are OFL-licensed woff2 from @fontsource, no
 * build-time Google Fonts network. */
const GOOGLE_SANS_FACE = [400, 500, 700]
  .map(
    (w) =>
      `@font-face{font-family:'Google Sans';font-style:normal;font-weight:${w};font-display:swap;src:url('/fonts/google-sans-latin-${w}.woff2') format('woff2');}`,
  )
  .join('')

type NotificationStyle = 'ios-lock' | 'ios-banner' | 'android'

interface NotificationState {
  style: NotificationStyle
  appName: string
  title: string
  body: string
  /** Clock shown in the status bar / lock-screen ("9:41"). */
  time: string
  /** Relative time shown on the notification itself ("now", "5m ago"). */
  relativeTime: string
  /** Lock-screen big-clock date line. */
  dateLine: string
  /** Accent colour for the generated (no-upload) app icon. */
  iconColor: string
}

// Neutral placeholder defaults (demo-trust).
const DEFAULT_STATE: NotificationState = {
  style: 'ios-lock',
  appName: 'Your App',
  title: 'New message',
  body: 'Tap to open — your notification copy goes here.',
  time: '9:41',
  relativeTime: 'now',
  dateLine: 'Monday, June 8',
  iconColor: '#6366f1',
}

const ICON_COLORS = ['#6366f1', '#ec4899', '#f97316', '#22c55e', '#0ea5e9', '#ef4444', '#a855f7', '#111827'] as const

const STYLES: ReadonlyArray<{ id: NotificationStyle; label: string }> = [
  { id: 'ios-lock', label: 'iOS Lock Screen' },
  { id: 'ios-banner', label: 'iOS Banner' },
  { id: 'android', label: 'Android' },
]

/** 10 MB upload ceiling — mirrors the other generators (unbounded data URL = OOM). */
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024

function firstLetter(name: string): string {
  const ch = name.trim()[0]
  return ch ? ch.toUpperCase() : '?'
}

interface NotificationGeneratorProps {
  trackingSlug?: string
}

export function NotificationGenerator({
  trackingSlug = 'notification-generator',
}: NotificationGeneratorProps): JSX.Element {
  const [state, setState] = useState<NotificationState>(DEFAULT_STATE)
  const [appIcon, setAppIcon] = useState<string | null>(null)
  // Shared export lifecycle: warm html2canvas on mount, re-entrancy guard,
  // and the `isExporting` flag for the Download affordance.
  const { isExporting, runExport } = useExport()

  // Render the iOS/Android phone-screen preview from a `useDeferredValue`
  // snapshot so copy keystrokes commit synchronously (inputs read live `state`)
  // while the preview re-renders at a lower priority — the same boundary the
  // other generators use to shorten the long interaction task behind the
  // generator pages' mobile INP. Export waits for the snapshot to converge.
  const { deferred: deferredState, isStale: stateStale } = usePreviewDeferral(state)
  const { deferred: deferredAppIcon, isStale: iconStale } = usePreviewDeferral(appIcon)
  const previewStaleRef = useStaleRef(stateStale || iconStale)

  const previewRef = useRef<HTMLDivElement>(null)
  const [getTotalEngagementMs, getDeltaEngagementMs] = useMemo(() => createEngagementTimers(), [])

  const update = useCallback(<K extends keyof NotificationState>(key: K, value: NotificationState[K]): void => {
    setState(prev => ({ ...prev, [key]: value }))
  }, [])

  const handleIcon = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
    const input = event.target
    const file = input.files?.[0]
    if (!file) return
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error('Image is too large (max 10 MB). Please choose a smaller file.')
      input.value = ''
      return
    }
    const reader = new FileReader()
    reader.onerror = () => {
      toast.error('Could not read that image. Please try another file.')
      input.value = ''
    }
    reader.onload = () => {
      setAppIcon(typeof reader.result === 'string' ? reader.result : null)
      sendResourceEvent('interact', {
        resourceType: 'tool',
        resourceSlug: trackingSlug,
        engagementMs: getDeltaEngagementMs(),
        params: { tool_type: 'generator', interaction_type: 'upload_icon', file_type: file.type, file_size: file.size },
      })
    }
    reader.readAsDataURL(file)
  }, [getDeltaEngagementMs, trackingSlug])

  const reset = useCallback((): void => {
    setState(DEFAULT_STATE)
    setAppIcon(null)
  }, [])

  const exportPng = useCallback((): Promise<void> => runExport(async beginCapture => {
    if (!previewRef.current) {
      toast.error('Preview not found. Please ensure the mockup is visible.')
      return
    }
    // Preview renders from `deferredState`; wait for it to converge so the
    // exported PNG matches the live controls (and the filename's style); if it
    // can't settle within the ceiling, fail loudly rather than export stale.
    // Flip the loading state only AFTER the gate so it tracks the capture.
    const stabilized = await waitForStable(() => !previewStaleRef.current)
    if (!stabilized) {
      toast.error('Preview is still updating — pause a moment, then export again.')
      return
    }
    beginCapture()
    await captureAndDownload({
      element: previewRef.current,
      format: 'png',
      filename: `${state.style}-notification`,
      trackingSlug,
      engagementMs: getTotalEngagementMs(),
      extraParams: { style: state.style },
      onSuccess: () => toast.success('Notification exported!'),
      onError: () => toast.error('Failed to export. Please try again.'),
    })
  }), [runExport, getTotalEngagementMs, state.style, trackingSlug, previewStaleRef])

  const inputClasses =
    'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition-colors focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white'

  // Preview reads the deferred style so the rendered screen matches the
  // committed snapshot, not a style switch that hasn't reached the preview yet.
  const previewIsIos = deferredState.style !== 'android'

  // Memoize the preview ELEMENT so the per-keystroke urgent re-render (deferred*
  // values referentially unchanged) reuses it and React bails out of re-rendering
  // the heavy phone-screen preview — it then renders only on the low-priority
  // deferred pass. Without this, useDeferredValue double-renders and *raises* INP.
  const notificationPreview = useMemo(
    () => previewIsIos
      ? <IosScreen state={deferredState} appIcon={deferredAppIcon} />
      : <AndroidScreen state={deferredState} appIcon={deferredAppIcon} />,
    [previewIsIos, deferredState, deferredAppIcon],
  )

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 px-1 sm:px-0 lg:flex-row lg:items-start">
      {/* ── Controls ─────────────────────────────────────────────── */}
      <section className="flex-1 rounded-3xl border border-slate-200/70 bg-white/80 p-6 shadow-2xl dark:border-slate-800/70 dark:bg-slate-900/70">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50">Design your notification</h2>

        {/* Style toggle — segmented-control grid (matches the other generators). */}
        <div className="mt-4">
          <div className="grid grid-cols-3 gap-1.5 rounded-2xl bg-slate-100 p-1.5 dark:bg-slate-800/60">
            {STYLES.map(s => (
              <button
                key={s.id}
                type="button"
                onClick={() => update('style', s.id)}
                aria-pressed={state.style === s.id}
                className={clsx(
                  'rounded-xl px-3 py-2 text-center text-sm font-medium transition',
                  state.style === s.id
                    ? 'bg-white text-indigo-600 shadow-sm dark:bg-slate-900 dark:text-indigo-300'
                    : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white',
                )}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          <Field label="App name" htmlFor="nt-appName">
            <input id="nt-appName" className={inputClasses} value={state.appName} maxLength={30}
              onChange={e => update('appName', e.target.value)} />
          </Field>
          <Field label="Time on notification" htmlFor="nt-relativeTime" hint='e.g. "now", "5m ago", "8:30 AM"'>
            <input id="nt-relativeTime" className={inputClasses} value={state.relativeTime} maxLength={12}
              onChange={e => update('relativeTime', e.target.value)} />
          </Field>

          <Field label="Title" htmlFor="nt-title" full>
            <input id="nt-title" className={inputClasses} value={state.title} maxLength={60}
              onChange={e => update('title', e.target.value)} />
          </Field>
          <Field label="Body" htmlFor="nt-body" full>
            <textarea id="nt-body" className={clsx(inputClasses, 'min-h-[80px] resize-y')} value={state.body} maxLength={240}
              onChange={e => update('body', e.target.value)} />
          </Field>

          <Field label="Status-bar clock" htmlFor="nt-time" hint="Shown in the status bar (and big clock on the lock screen).">
            <input id="nt-time" className={inputClasses} value={state.time} maxLength={8}
              onChange={e => update('time', e.target.value)} />
          </Field>
          {state.style !== 'ios-banner' && (
            <Field label="Date" htmlFor="nt-dateLine" hint="Shown under the big clock on the lock screen.">
              <input id="nt-dateLine" className={inputClasses} value={state.dateLine} maxLength={40}
                onChange={e => update('dateLine', e.target.value)} />
            </Field>
          )}

          <Field label="App icon (optional)">
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-600 transition hover:border-indigo-400 hover:text-indigo-600 dark:border-slate-600 dark:text-slate-300">
              <PhotoIcon className="h-5 w-5" />
              {appIcon ? 'Replace icon' : 'Upload icon'}
              <input type="file" accept="image/*" aria-label="Upload app icon image" className="sr-only"
                onChange={handleIcon} />
            </label>
          </Field>
          <Field label="Icon color (if no upload)">
            <div className="flex h-[42px] items-center gap-2">
              {ICON_COLORS.map(c => (
                <button key={c} type="button" aria-label={`Icon color ${c}`} aria-pressed={state.iconColor === c}
                  onClick={() => update('iconColor', c)}
                  className={clsx('h-6 w-6 rounded-full border transition', state.iconColor === c ? 'ring-2 ring-indigo-500 ring-offset-2 dark:ring-offset-slate-900' : 'border-black/10')}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
          </Field>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-slate-200/70 pt-5 dark:border-slate-700/70">
          <ExportButton
            isExporting={isExporting}
            onExport={() => void exportPng()}
            className="rounded-full px-6 py-2 text-sm font-semibold text-white shadow-sm transition"
            idleClassName="bg-indigo-600 hover:bg-indigo-700"
            exportingClassName="cursor-not-allowed bg-indigo-400"
          />
          <button type="button" onClick={reset}
            className="flex items-center gap-1.5 rounded-full border border-slate-300/70 px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-red-200 hover:text-red-600 dark:border-slate-600 dark:text-slate-200">
            <ArrowPathIcon className="h-4 w-4" /> Reset
          </button>
        </div>
      </section>

      {/* ── Preview ──────────────────────────────────────────────── */}
      <aside className="lg:sticky lg:top-24 lg:w-[26rem]">
        <p className="mb-3 text-center text-xs font-medium uppercase tracking-wide text-slate-400">Live preview</p>
        <div className="flex justify-center">
          <div ref={previewRef} className="w-full max-w-[320px]">
            {notificationPreview}
          </div>
        </div>
        <p className="mt-3 text-center text-xs text-slate-400">
          For ad creative, product shots, and mockups — not a real system alert. Don&apos;t present it as a genuine notification from a brand you don&apos;t represent.
        </p>
      </aside>
    </div>
  )
}

function Field({ label, htmlFor, full, hint, children }: { label: string; htmlFor?: string; full?: boolean; hint?: string; children: React.ReactNode }): JSX.Element {
  const headingClass = 'block text-sm font-medium text-slate-700 dark:text-slate-200'
  return (
    <div className={clsx('space-y-1.5', full && 'sm:col-span-2')}>
      {htmlFor ? <label htmlFor={htmlFor} className={headingClass}>{label}</label> : <span className={headingClass}>{label}</span>}
      {children}
      {hint ? <p className="text-xs text-slate-400 dark:text-slate-500">{hint}</p> : null}
    </div>
  )
}

/** App icon block — uploaded image or a coloured rounded square with the first letter. */
function AppIcon({ name, image, color, size, radius }: { name: string; image: string | null; color: string; size: number; radius: number }): JSX.Element {
  return (
    <div className="flex flex-shrink-0 items-center justify-center overflow-hidden font-semibold text-white"
      style={{ width: size, height: size, borderRadius: radius, backgroundColor: image ? undefined : color, fontSize: size * 0.5 }}>
      {image ? (
        // eslint-disable-next-line @next/next/no-img-element -- user-uploaded data URL; next/image can't optimize data URIs and html2canvas needs the raw <img>
        <img src={image} alt={`${name} icon`} className="h-full w-full object-cover" />
      ) : (
        <span>{firstLetter(name)}</span>
      )}
    </div>
  )
}

/** iOS lock-screen and banner share the phone frame + status bar; the lock
 * screen adds the big clock and a wallpaper, the banner floats over a neutral
 * dimmed screen. */
function IosScreen({ state, appIcon }: { state: NotificationState; appIcon: string | null }): JSX.Element {
  const lock = state.style === 'ios-lock'
  return (
    <div className="relative aspect-[9/19] w-full overflow-hidden rounded-[44px] bg-black p-[3px] shadow-2xl">
      {/* Flex column (not absolute positioning) so the card lands reliably under
          html2canvas — `bottom`-anchored absolute elements are mis-placed on
          export, which clipped the card's title/body. `mt-auto` pushes the lock
          card to the bottom with `pb-12` clearing the rounded-[41px] corner. */}
      <div
        className={clsx('relative flex h-full w-full flex-col overflow-hidden rounded-[41px]', lock ? '' : 'bg-gradient-to-b from-slate-200 to-slate-300')}
        style={lock ? { backgroundImage: 'linear-gradient(160deg,#1e1b4b 0%,#4c1d95 45%,#9d174d 100%)' } : undefined}
      >
        {/* Dynamic Island — the camera/sensor pill the status bar reserves
            clearance for. Centred so the status-bar clock/icons flank it. */}
        <div className="absolute left-1/2 top-[11px] z-20 h-[26px] w-[86px] -translate-x-1/2 rounded-full bg-black" aria-hidden />
        <IosStatusBar time={state.time} tone={lock ? 'light' : 'dark'} />

        {lock ? (
          <>
            {/* Lock glyph + date + big clock — the real iOS 16/17 lock-screen
                stack (a generic "phone + clock" misses the padlock). */}
            <div className="mt-1 flex justify-center text-white/85">
              <LockGlyph />
            </div>
            <div className="mt-1 select-none text-center text-white">
              <div className="text-[15px] font-medium tracking-wide opacity-90">{state.dateLine}</div>
              <div className="text-[68px] font-semibold leading-none tracking-tight">{state.time}</div>
            </div>
            {/* mt-auto pushes the notification + bottom chrome to the bottom. */}
            <div className="mt-auto px-3">
              <IosNotificationCard state={state} appIcon={appIcon} />
            </div>
            {/* Flashlight + Camera quick actions — sit near the bottom corners
                on a real lock screen, so hug the edges (small inset). */}
            <div className="mt-5 flex items-center justify-between px-5">
              <QuickAction label="Flashlight"><FlashlightGlyph /></QuickAction>
              <QuickAction label="Camera"><CameraGlyph /></QuickAction>
            </div>
            {/* Home indicator. */}
            <div className="mx-auto mb-2 mt-3 h-1 w-1/3 rounded-full bg-white/60" />
          </>
        ) : (
          <div className="px-3 pt-2">
            <IosNotificationCard state={state} appIcon={appIcon} />
          </div>
        )}
      </div>
    </div>
  )
}

/** Small padlock shown at the top of the iOS lock screen. */
function LockGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor" aria-hidden>
      <path d="M12 2a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-1V7a5 5 0 0 0-5-5Zm3 8H9V7a3 3 0 1 1 6 0v3Z" />
    </svg>
  )
}

/** Flashlight torch glyph for the lock-screen quick-action button. */
function FlashlightGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden>
      <path d="M8.5 2h7l-1 4.5h-5L8.5 2Zm1.1 6h4.8l-.55 12.1a1.85 1.85 0 0 1-3.7 0L9.6 8Zm2.4 3.2a1 1 0 0 0-1 1v2.2a1 1 0 1 0 2 0v-2.2a1 1 0 0 0-1-1Z" />
    </svg>
  )
}

/** Camera glyph for the lock-screen quick-action button — body with a real
 * lens cut-out (evenodd) rather than a solid dot. */
function CameraGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" fillRule="evenodd" clipRule="evenodd" aria-hidden>
      <path d="M9.2 4a1.5 1.5 0 0 0-1.25.67L7.1 6H4.6A2.6 2.6 0 0 0 2 8.6v8.2A2.6 2.6 0 0 0 4.6 19.4h14.8A2.6 2.6 0 0 0 22 16.8V8.6A2.6 2.6 0 0 0 19.4 6h-2.5l-.85-1.33A1.5 1.5 0 0 0 14.8 4H9.2Zm2.8 4.6a4.1 4.1 0 1 0 0 8.2 4.1 4.1 0 0 0 0-8.2Z" />
    </svg>
  )
}

/** Frosted circular lock-screen quick-action button (Flashlight / Camera). */
function QuickAction({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <span aria-label={label} className="flex h-11 w-11 items-center justify-center rounded-full bg-black/30 text-white backdrop-blur-md">
      {children}
    </span>
  )
}

/** The frosted iOS notification card (iOS 16/17 style). */
function IosNotificationCard({ state, appIcon }: { state: NotificationState; appIcon: string | null }): JSX.Element {
  return (
    <div className="flex items-start gap-2.5 rounded-[18px] bg-white/85 px-3 py-3 shadow-sm backdrop-blur-xl">
      <AppIcon name={state.appName} image={appIcon} color={state.iconColor} size={38} radius={9} />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          {/* No `truncate` (overflow:hidden) — html2canvas clips the title's
              ascenders on export. Titles are short; a long one wraps. */}
          <span className="min-w-0 text-[15px] font-semibold leading-snug text-black">{state.title}</span>
          <span className="flex-shrink-0 text-[13px] leading-snug text-black/50">{state.relativeTime}</span>
        </div>
        {/* Plain wrapping paragraph (no `-webkit-line-clamp`): html2canvas
            mis-measures the `-webkit-box` clamp and clips the card on export. */}
        <p className="mt-0.5 whitespace-pre-wrap text-[15px] leading-snug text-black/80">{state.body}</p>
      </div>
    </div>
  )
}

/** Android (Pixel) system font stack. "Google Sans" is self-hosted via
 * @fontsource (imported above), so it renders the real Pixel typeface
 * everywhere — including the PNG export — with Roboto (the Android system font)
 * and generic sans as graceful fallbacks. */
const ANDROID_FONT = '"Google Sans", Roboto, "Noto Sans", system-ui, sans-serif'

/** Android (Material 3) status bar — clock is on the LEFT, glyphs on the right. */
function AndroidStatusBar({ time }: { time: string }): JSX.Element {
  return (
    <div className="flex h-9 select-none items-center justify-between px-4 text-[13px] font-medium text-white">
      <span>{time}</span>
      <div className="flex items-center gap-1.5" aria-hidden>
        {/* signal */}
        <span className="flex items-end gap-0.5">
          <span className="h-1.5 w-0.5 rounded-sm bg-white" />
          <span className="h-2 w-0.5 rounded-sm bg-white" />
          <span className="h-2.5 w-0.5 rounded-sm bg-white" />
          <span className="h-3 w-0.5 rounded-sm bg-white" />
        </span>
        {/* wifi */}
        <svg width="15" height="11" viewBox="0 0 16 12" fill="none"><path d="M8 2C4.5 2 1.3 3.6 0 5.6l8 6 8-6C14.7 3.6 11.5 2 8 2Z" fill="#fff" /></svg>
        {/* battery — Pixel renders the % INSIDE a filled glyph (Google Sans),
            dark digits on the charged body, with a short terminal nub. */}
        <span className="flex items-center">
          <span className="flex h-[12px] w-[23px] items-center justify-center rounded-[3.5px] bg-white">
            <span className="text-[8px] font-medium leading-none text-black">100</span>
          </span>
          <span className="ml-[1.5px] h-[5px] w-[2px] rounded-r-[1px] bg-white" />
        </span>
      </div>
    </div>
  )
}

/** Android (Material) notification on a dimmed lock/home screen. */
function AndroidScreen({ state, appIcon }: { state: NotificationState; appIcon: string | null }): JSX.Element {
  return (
    <div className="relative aspect-[9/19] w-full overflow-hidden rounded-[40px] bg-black p-[3px] shadow-2xl">
      {/* Route-scoped self-hosted Google Sans (see GOOGLE_SANS_FACE). */}
      <style dangerouslySetInnerHTML={{ __html: GOOGLE_SANS_FACE }} />
      <div className="relative h-full w-full overflow-hidden rounded-[37px]" style={{ backgroundImage: 'linear-gradient(170deg,#0f172a 0%,#1e293b 60%,#334155 100%)', fontFamily: ANDROID_FONT }}>
        {/* Centred punch-hole front camera — the Android equivalent of the notch. */}
        <div className="absolute left-1/2 top-[9px] z-20 h-2.5 w-2.5 -translate-x-1/2 rounded-full bg-black ring-1 ring-white/10" aria-hidden />
        <AndroidStatusBar time={state.time} />
        {/* big clock — Android lock clock is left-aligned, light weight */}
        <div className="mt-4 select-none px-5 text-white">
          <div className="text-[64px] font-light leading-none tracking-tight">{state.time}</div>
          <div className="mt-1 text-[14px] opacity-90">{state.dateLine}</div>
        </div>
        {/* Material notification card */}
        <div className="absolute inset-x-3 top-[150px]">
          <div className="rounded-[26px] bg-[#1f2937]/95 px-4 py-3 shadow-lg">
            <div className="flex items-center gap-2 text-[12px] text-white/70">
              <AppIcon name={state.appName} image={appIcon} color={state.iconColor} size={20} radius={999} />
              <span className="truncate font-medium uppercase tracking-wide">{state.appName}</span>
              <span aria-hidden>•</span>
              <span className="flex-shrink-0">{state.relativeTime}</span>
            </div>
            <div className="mt-1.5 text-[15px] font-semibold leading-snug text-white">{state.title}</div>
            <p className="mt-0.5 whitespace-pre-wrap text-[14px] leading-snug text-white/80">{state.body}</p>
          </div>
        </div>
      </div>
    </div>
  )
}
